import type { ApiError, ApiResponse, ErrorCode } from '@cgp/shared'
import { useAuthStore } from '../stores/auth.store'
import i18next from '../i18n'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000/v1'

/** Thrown for every non-2xx response, carrying the contract error code. */
export class ApiRequestError extends Error {
  constructor(
    readonly code: ErrorCode,
    readonly status: number,
    readonly messageEn: string,
    readonly messageVi: string,
    readonly details?: Array<Record<string, unknown>>,
    readonly traceId?: string,
  ) {
    super(`${code}: ${messageEn}`)
    this.name = 'ApiRequestError'
  }

  /** Message in the user's active language — never a raw English fallback. */
  get localizedMessage(): string {
    return i18next.resolvedLanguage === 'en' ? this.messageEn : this.messageVi
  }
}

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  /** Required for state-changing calls; enables safe retries. */
  idempotencyKey?: string
  signal?: AbortSignal
  query?: Record<string, string | number | boolean | undefined>
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${BASE_URL}${path}`)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value))
  }
  return url.toString()
}

function authHeaders(): Record<string, string> {
  const { accessToken } = useAuthStore.getState()
  const headers: Record<string, string> = {
    'Accept-Language': i18next.resolvedLanguage ?? 'vi',
  }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  return headers
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
  const method = options.method ?? 'GET'

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...authHeaders(),
  }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey
  else if (method !== 'GET' && method !== 'DELETE') {
    // Generate one automatically so a retry can never double-apply a write.
    headers['Idempotency-Key'] = crypto.randomUUID()
  }

  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers,
    credentials: 'include',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  if (response.status === 204) return { data: null as T }

  const payload = (await response.json()) as ApiResponse<T> | ApiError

  if (!response.ok) {
    const error = (payload as ApiError).error
    throw new ApiRequestError(
      error?.code ?? 'INTERNAL_ERROR',
      response.status,
      error?.messageEn ?? 'Request failed',
      error?.messageVi ?? 'Yêu cầu thất bại',
      error?.details,
      error?.traceId,
    )
  }

  return payload as ApiResponse<T>
}

/**
 * Fetches a binary file (e.g. an .xlsx report export) and triggers a browser
 * download. Separate from `apiRequest` because that helper always parses the
 * response body as the `{ data }` JSON envelope, which a file download never
 * uses.
 */
export async function apiDownload(path: string, query?: RequestOptions['query']): Promise<void> {
  const response = await fetch(buildUrl(path, query), {
    method: 'GET',
    headers: authHeaders(),
    credentials: 'include',
  })

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as ApiError | null
    const error = payload?.error
    throw new ApiRequestError(
      error?.code ?? 'INTERNAL_ERROR',
      response.status,
      error?.messageEn ?? 'Request failed',
      error?.messageVi ?? 'Yêu cầu thất bại',
      error?.details,
      error?.traceId,
    )
  }

  const blob = await response.blob()
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const filenameMatch = /filename="?([^";]+)"?/.exec(disposition)
  const filename = filenameMatch?.[1] ?? 'report.xlsx'

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
